import { useLayoutEffect } from 'react';

import { useCurrentRoleTaxonomyVersion } from '../../../shared/selection-lists/hooks/useRoleTaxonomyAdapter';
import { useCurrentSkillTaxonomyVersion } from '../../../shared/selection-lists/hooks/useSkillTaxonomyAdapter';
import {
  usePageConfig,
  useTabContentConfig,
} from '../components/PageConfigProvider/PageConfigProvider';
import { useFilterContext } from '../filters/contexts/FilterProvider';
import {
  type ConsistencyKey,
  generatePageKey,
  getOrCreateFilters,
  NO_PRIMARY_FILTER_KEY,
} from '../filters/engine/filterGroupConsistency';
import { getPrimaryFilterIdForScreenerV2Page } from '../utils/routing-helpers';

export const useSetNewFilterPageMap = () => {
  const { filterState, dispatch: filterDispatch } = useFilterContext();
  const { config, revelioPageId } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();

  const { version: roleTaxonomyVersion } = useCurrentRoleTaxonomyVersion();
  const { version: skillTaxonomyVersion } = useCurrentSkillTaxonomyVersion();

  const basePrimaryFilterId = config.primaryFilter?.id ?? NO_PRIMARY_FILTER_KEY;

  // For screener v2 pages, use the page ID as the consistency key to isolate their filters
  // from other pages (e.g., company screener v2 doesn't share state with company compositions)
  const isScreenerV2 = getPrimaryFilterIdForScreenerV2Page(revelioPageId);
  const primaryFilterId: ConsistencyKey =
    isScreenerV2 && revelioPageId ? revelioPageId : basePrimaryFilterId;

  const filterMap = getOrCreateFilters(filterState, primaryFilterId, {
    roleTaxonomyVersion,
    skillTaxonomyVersion,
  });

  const pageKey = generatePageKey(
    primaryFilterId,
    revelioPageId ?? pageConfig.id
  );

  // Use layout effect so the active filter group switches before the user can
  // interact with UI (prevents applying preferences/updates to the wrong group).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useLayoutEffect(() => {
    filterDispatch({
      type: 'REPLACE_FILTERS',
      payload: {
        filterMap,
        pageKey,
        defaultActiveCount: config.primaryFilter?.selectionDefault,
      },
    });
  }, [pageKey]);
};
